import { Injectable } from '@angular/core';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { EnvironmentService, SplitService } from '@patient-ui/shared-ui/utils';

import { OrbiFeatureActions } from './orbi-feature.actions';
import * as AuthActions from '../auth/auth.actions';

/** Split.io flag controlling the web AI agent (PCSA-13165). */
export const ORBI_AGENT_FLAG = 'web-ai-agent';

/**
 * Evaluates the Orbi agent Split flag and reflects it into the orbi-feature
 * slice. Triggered on authentication (and on an explicit load action). Uses
 * `defaultOn = false` so the agent stays OFF when the flag is unavailable /
 * `control` — fail-closed for an unreleased feature. `isOn$` is a live stream
 * (re-emits on SDK_UPDATE), so a mid-session flag toggle updates the slice; the
 * UI stories (13157/13158) react to the selector. The stream lasts as long as the
 * SESSION, not as long as the app — see the `takeUntil` below.
 */
@Injectable()
export class OrbiFeatureEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private splitService: SplitService,
    private envService: EnvironmentService
  ) {}

  /**
   * Evaluate the flag once on every app bootstrap (including a page refresh /
   * session restore, which does NOT re-dispatch `authenticateSuccess`). Without
   * this the agent would disappear after a refresh.
   */
  ngrxOnInitEffects(): Action {
    return OrbiFeatureActions.loadOrbiAgentFlag();
  }

  loadOrbiAgentFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.authenticateSuccess,
        OrbiFeatureActions.loadOrbiAgentFlag
      ),
      switchMap(() =>
        this.splitService
          // Default comes from env config (`orbiAgentDefault`) — false in
          // deployed envs (agent OFF until the Split flag is created), can be
          // set true in local assets/environment.json to preview the agent.
          .isOn$(ORBI_AGENT_FLAG, this.envService.orbiAgentDefault)
          .pipe(
            map((enabled) =>
              OrbiFeatureActions.setOrbiAgentEnabled({ enabled })
            ),
            // Stop at sign-out (QA 08/21). `isOn$` is a LIVE stream — it re-emits
            // on every Split SDK_UPDATE (features refresh on a 15s timer) — and
            // `switchMap` keeps it subscribed for the app's lifetime. Without this
            // the next re-emission re-dispatches `setOrbiAgentEnabled(true)`
            // seconds after logout cleared it, silently undoing the reducer's
            // reset for a patient who is no longer there. The Split client is
            // keyed 'anonymous', so its treatment cannot tell us that itself.
            //
            // The stream is re-established on the next `authenticateSuccess`, and
            // on a refresh by `ngrxOnInitEffects` above, so nothing is lost.
            takeUntil(
              this.actions$.pipe(
                ofType(
                  AuthActions.logOut,
                  AuthActions.logOutSuccess,
                  AuthActions.logOutFailure
                )
              )
            )
          )
      )
    )
  );
}
